'use client'

import {useCallback, useState} from 'react'
import debounce from 'lodash/debounce'

export interface UseDebouncedPaginationOptions {
  /**
   * Initial page number (default: 1)
   */
  initialPage?: number
  /**
   * Debounce delay in milliseconds (default: 300)
   */
  debounceDelay?: number
  /**
   * Callback to be called when page changes
   */
  onPageChange?: (page: number) => void
}

/**
 * A hook that provides debounced pagination functionality
 * to prevent rapid API calls when users quickly navigate through pages
 */
export function useDebouncedPagination({initialPage = 1, debounceDelay = 300, onPageChange}: UseDebouncedPaginationOptions = {}) {
  const [page, setPage] = useState(initialPage)

  const debouncedSetPage = useCallback(
    debounce((newPage: number) => {
      setPage(newPage)
      onPageChange?.(newPage)
    }, debounceDelay),
    [debounceDelay, onPageChange]
  )

  return {
    page,
    setPage: debouncedSetPage
  }
}
